import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";

const TermsOfService = () => {
  return (
    <div className="min-h-[100dvh] gradient-hero">
      <SEO
        title="Terms of Service"
        description="The terms governing use of VIZ — your account, subscription, AI-generated visualization content, and the limits of the service for athletes."
        path="/terms"
        noIndex
      />
      <div className="container max-w-3xl px-6 py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <h1 className="font-display text-4xl font-bold mb-2">Terms of Service</h1>
        <p className="text-muted-foreground mb-10">Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>

        <div className="prose prose-invert max-w-none space-y-8 text-muted-foreground">
          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">1. Acceptance of Terms</h2>
            <p>By accessing or using VIZ ("the Service"), you agree to be bound by these Terms of Service. If you do not agree, please do not use the Service.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">2. Description of Service</h2>
            <p>VIZ is an AI-powered mental training platform for athletes. The Service generates personalized visualization scripts and audio sessions based on user-provided information. VIZ is not a substitute for professional medical or psychological advice.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">3. Account Registration</h2>
            <p>You must provide accurate, complete information when creating an account. You are responsible for maintaining the security of your account credentials. You must be at least 13 years old to use the Service.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">4. Free Trial & Subscriptions</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>New users receive a 7-day free trial with limited weekly credits.</li>
              <li>Paid subscriptions unlock additional credits and features.</li>
              <li>Subscriptions renew automatically unless canceled before the renewal date.</li>
              <li>Refunds are handled in accordance with applicable law and Stripe's policies.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">5. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Use the Service for any unlawful purpose</li>
              <li>Attempt to reverse-engineer or extract AI models or proprietary content</li>
              <li>Share, redistribute, or commercially resell generated audio content</li>
              <li>Abuse the credit system or create multiple accounts to circumvent limits</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">6. Intellectual Property</h2>
            <p>All AI-generated scripts and audio are provided for your personal, non-commercial use. VIZ retains ownership of the underlying platform, models, and technology. You retain ownership of the personal information you provide.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">7. Disclaimer of Warranties</h2>
            <p>The Service is provided "as is" without warranties of any kind. VIZ does not guarantee specific athletic performance outcomes. AI-generated content may occasionally contain inaccuracies.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">8. Limitation of Liability</h2>
            <p>To the maximum extent permitted by law, VIZ shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Service.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">9. Account Termination</h2>
            <p>You may delete your account at any time from your Profile page. We reserve the right to suspend or terminate accounts that violate these terms.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">10. Changes to Terms</h2>
            <p>We may update these Terms from time to time. Continued use of the Service after changes constitutes acceptance of the new Terms.</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground">11. Contact</h2>
            <p>Questions about these Terms? Contact us at <a href="mailto:support@viz.app" className="text-primary hover:underline">support@viz.app</a>.</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
